import { ServiceLocationHierarchy } from '../../../../../@types/service.type';

export interface HierarchyFilterOptions {
  /** Case-insensitive substring match against node names. Empty/whitespace = no name filter. */
  searchTerm: string;
  /** Keep only nodes that themselves have a job, or have a descendant that does. */
  requireJobs: boolean;
  /** Keep only nodes that themselves have a part, or have a descendant that does. Independent
   *  of `requireJobs` — ticking both requires a node to have BOTH a job and a part itself
   *  (not one of each spread across different descendants). */
  requireParts: boolean;
}

export const isHierarchyFilterActive = ({
  searchTerm,
  requireJobs,
  requireParts,
}: HierarchyFilterOptions): boolean => Boolean(searchTerm.trim()) || requireJobs || requireParts;

const matchesFilter = (
  node: ServiceLocationHierarchy,
  { searchTerm, requireJobs, requireParts }: HierarchyFilterOptions
): boolean => {
  const term = searchTerm.trim().toLowerCase();
  const nameMatches = !term || node.name.toLowerCase().includes(term);
  const jobsMatch = !requireJobs || node.serviceCount > 0;
  const partsMatch = !requireParts || node.partCount > 0;
  return nameMatches && jobsMatch && partsMatch;
};

/**
 * Prunes the tree down to nodes that match the filter, plus every ancestor
 * of a match (so the path/context to a result is never lost). Returns null
 * when neither the node nor any descendant matches.
 *
 * A node's children are always replaced with the filtered set — even a
 * matching node hides its own non-matching children, rather than showing
 * everything underneath once one thing matches. That keeps a search or
 * "has jobs"/"has parts" result from re-exploding back out to the full
 * subtree.
 */
export const filterHierarchyTree = (
  node: ServiceLocationHierarchy | null,
  options: HierarchyFilterOptions
): ServiceLocationHierarchy | null => {
  if (!node) return null;

  const filteredChildren = (node.children ?? [])
    .map((child) => filterHierarchyTree(child, options))
    .filter((child): child is ServiceLocationHierarchy => child !== null);

  if (!matchesFilter(node, options) && filteredChildren.length === 0) return null;

  return { ...node, children: filteredChildren };
};
